/**
 * RealSEODashboard - Shows REAL data with clear source labels
 * Tabs: PageSpeed | Keywords | Questions | Site Crawl
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Zap, Globe, AlertTriangle, CheckCircle, Loader2, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

function SourceBadge({ source, type }) {
  const color = type === "REAL" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700";
  return <Badge className={`${color} text-xs`}>{type}: {source}</Badge>;
}

function ScoreCircle({ score, label }) {
  const color = score >= 90 ? "text-green-600" : score >= 50 ? "text-orange-500" : "text-red-500";
  return (
    <div className="text-center">
      <div className={`text-3xl font-bold ${color}`}>{score}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

export default function RealSEODashboard() {
  const [url, setUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [pagespeed, setPagespeed] = useState(null);
  const [keywords, setKeywords] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});

  const fetchData = async (key, endpoint) => {
    setLoading(l => ({ ...l, [key]: true }));
    setErrors(e => ({ ...e, [key]: null }));
    try {
      const res = await fetch(endpoint, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Request failed" }));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      return await res.json();
    } catch (e) {
      setErrors(er => ({ ...er, [key]: e.message }));
      return null;
    } finally {
      setLoading(l => ({ ...l, [key]: false }));
    }
  };

  const runPageSpeed = async () => {
    if (!url.trim()) return;
    const data = await fetchData("pagespeed", `${API}/api/pagespeed/analyze?url=${encodeURIComponent(url)}&strategy=mobile`);
    if (data) setPagespeed(data);
  };

  const runKeywords = async () => {
    if (!keyword.trim()) return;
    const [kw, q] = await Promise.all([
      fetchData("keywords", `${API}/api/keywords/real/autocomplete?q=${encodeURIComponent(keyword)}&country=in`),
      fetchData("questions", `${API}/api/keywords/real/questions?q=${encodeURIComponent(keyword)}&country=in`),
    ]);
    if (kw) setKeywords(kw);
    if (q) setQuestions(q);
  };

  return (
    <div className="space-y-6" data-testid="real-seo-dashboard">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 items-start">
        <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-800">
          <strong>Real Data Only</strong> — All data on this page comes from verified sources (Google APIs, website crawl). 
          Look for <Badge className="bg-green-100 text-green-700 text-xs mx-1">REAL</Badge> badges. 
          AI is only used for suggestions, never for metrics.
        </div>
      </div>

      <Tabs defaultValue="pagespeed">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pagespeed">PageSpeed Audit</TabsTrigger>
          <TabsTrigger value="keywords">Keyword Ideas</TabsTrigger>
          <TabsTrigger value="questions">People Ask</TabsTrigger>
        </TabsList>

        {/* PageSpeed Tab */}
        <TabsContent value="pagespeed" className="space-y-4 mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-2">
                <Input placeholder="Enter URL (e.g., codewithkamlesh.in)" value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && runPageSpeed()} data-testid="pagespeed-url-input" />
                <Button onClick={runPageSpeed} disabled={loading.pagespeed} className="bg-violet-600 hover:bg-violet-700" data-testid="pagespeed-run-btn">
                  {loading.pagespeed ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                  Analyze
                </Button>
              </div>
              {errors.pagespeed && <p className="text-red-500 text-sm mt-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {errors.pagespeed}</p>}
            </CardContent>
          </Card>

          {pagespeed && (
            <>
              <div className="flex items-center gap-2">
                <SourceBadge source={pagespeed.source} type={pagespeed.data_type} />
                <span className="text-xs text-gray-400">Lighthouse v{pagespeed.lighthouse_version}</span>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4"><ScoreCircle score={pagespeed.scores.performance} label="Performance" /></Card>
                <Card className="p-4"><ScoreCircle score={pagespeed.scores.seo} label="SEO" /></Card>
                <Card className="p-4"><ScoreCircle score={pagespeed.scores.accessibility} label="Accessibility" /></Card>
                <Card className="p-4"><ScoreCircle score={pagespeed.scores.best_practices} label="Best Practices" /></Card>
              </div>

              {Object.keys(pagespeed.core_web_vitals || {}).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-sm">Core Web Vitals</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(pagespeed.core_web_vitals).map(([k, v]) => (
                        <div key={k} className="p-3 bg-gray-50 rounded-lg text-center">
                          <div className={`text-lg font-bold ${v.score >= 90 ? "text-green-600" : v.score >= 50 ? "text-orange-500" : "text-red-500"}`}>{v.value}</div>
                          <div className="text-xs text-gray-500">{k}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {pagespeed.opportunities?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-sm">Optimization Opportunities</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {pagespeed.opportunities.slice(0, 8).map((o, i) => (
                      <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span className="text-sm">{o.title}</span>
                        {o.savings_ms && <Badge variant="outline" className="text-xs">{Math.round(o.savings_ms)}ms savings</Badge>}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Keywords Tab */}
        <TabsContent value="keywords" className="space-y-4 mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-2">
                <Input placeholder="Enter seed keyword (e.g., seo tools)" value={keyword} onChange={e => setKeyword(e.target.value)} onKeyDown={e => e.key === "Enter" && runKeywords()} data-testid="keyword-input" />
                <Button onClick={runKeywords} disabled={loading.keywords} className="bg-violet-600 hover:bg-violet-700" data-testid="keyword-run-btn">
                  {loading.keywords ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  Research
                </Button>
              </div>
              {errors.keywords && <p className="text-red-500 text-sm mt-2">{errors.keywords}</p>}
            </CardContent>
          </Card>

          {keywords && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Google Autocomplete Suggestions ({keywords.count})</CardTitle>
                <SourceBadge source={keywords.source} type={keywords.data_type} />
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-400 mb-3">{keywords.note}</p>
                <div className="grid grid-cols-2 gap-2">
                  {keywords.suggestions.map((s, i) => (
                    <div key={i} className="p-2 bg-gray-50 rounded text-sm flex items-center gap-2">
                      <Search className="w-3 h-3 text-gray-400" />
                      {s}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Questions Tab */}
        <TabsContent value="questions" className="space-y-4 mt-4">
          {!questions && !keyword && (
            <Card className="p-8 text-center text-gray-400">
              <Globe className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Enter a keyword in the Keywords tab first, then switch here to see questions.</p>
            </Card>
          )}
          {questions && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">People Also Ask ({questions.count})</CardTitle>
                <SourceBadge source={questions.source} type={questions.data_type} />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {questions.questions.map((q, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded text-sm flex items-start gap-2">
                      <span className="text-violet-500 font-bold">Q</span>
                      {q}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
